import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetAllClientInvitationsQueryResponse, GetAllClientInvitationsPathParams, GetAllClientInvitationsQueryParams } from "../types/GetAllClientInvitations";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetAllClientInvitationsClient = typeof client<GetAllClientInvitationsQueryResponse, never, never>;
type GetAllClientInvitations = {
    data: GetAllClientInvitationsQueryResponse;
    error: never;
    request: never;
    pathParams: GetAllClientInvitationsPathParams;
    queryParams: GetAllClientInvitationsQueryParams;
    headerParams: never;
    response: GetAllClientInvitationsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetAllClientInvitationsClient>[0]>;
        return: Awaited<ReturnType<GetAllClientInvitationsClient>>;
    };
};
export const getAllClientInvitationsQueryKey = (clientCode: GetAllClientInvitationsPathParams["clientCode"], params?: GetAllClientInvitations["queryParams"]) => [{ url: "/api/clients/:clientCode/invitations", params: { clientCode: clientCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientInvitationsQueryKey = ReturnType<typeof getAllClientInvitationsQueryKey>;
export function getAllClientInvitationsQueryOptions(clientCode: GetAllClientInvitationsPathParams["clientCode"], params?: GetAllClientInvitations["queryParams"], options: GetAllClientInvitations["client"]["parameters"] = {}) {
    const queryKey = getAllClientInvitationsQueryKey(clientCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientInvitations["data"], GetAllClientInvitations["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/invitations`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all invitations for a given client and the state of the
respective invite tokens.
 * @summary Get all
 * @link /api/clients/:clientCode/invitations
 */
export function useGetAllClientInvitations<TData = GetAllClientInvitations["response"], TQueryData = GetAllClientInvitations["response"], TQueryKey extends QueryKey = GetAllClientInvitationsQueryKey>(clientCode: GetAllClientInvitationsPathParams["clientCode"], params?: GetAllClientInvitations["queryParams"], options: {
    query?: Partial<QueryObserverOptions<GetAllClientInvitations["response"], GetAllClientInvitations["error"], TData, TQueryData, TQueryKey>>;
    client?: GetAllClientInvitations["client"]["parameters"];
} = {}): UseQueryResult<TData, GetAllClientInvitations["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientInvitationsQueryKey(clientCode, params);
    const query = useQuery({
        ...getAllClientInvitationsQueryOptions(clientCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetAllClientInvitations["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getAllClientInvitationsSuspenseQueryKey = (clientCode: GetAllClientInvitationsPathParams["clientCode"], params?: GetAllClientInvitations["queryParams"]) => [{ url: "/api/clients/:clientCode/invitations", params: { clientCode: clientCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientInvitationsSuspenseQueryKey = ReturnType<typeof getAllClientInvitationsSuspenseQueryKey>;
export function getAllClientInvitationsSuspenseQueryOptions(clientCode: GetAllClientInvitationsPathParams["clientCode"], params?: GetAllClientInvitations["queryParams"], options: GetAllClientInvitations["client"]["parameters"] = {}) {
    const queryKey = getAllClientInvitationsSuspenseQueryKey(clientCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientInvitations["data"], GetAllClientInvitations["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/invitations`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all invitations for a given client and the state of the
respective invite tokens.
 * @summary Get all
 * @link /api/clients/:clientCode/invitations
 */
export function useGetAllClientInvitationsSuspense<TData = GetAllClientInvitations["response"], TQueryKey extends QueryKey = GetAllClientInvitationsSuspenseQueryKey>(clientCode: GetAllClientInvitationsPathParams["clientCode"], params?: GetAllClientInvitations["queryParams"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetAllClientInvitations["response"], GetAllClientInvitations["error"], TData, TQueryKey>>;
    client?: GetAllClientInvitations["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetAllClientInvitations["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientInvitationsSuspenseQueryKey(clientCode, params);
    const query = useSuspenseQuery({
        ...getAllClientInvitationsSuspenseQueryOptions(clientCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetAllClientInvitations["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}